import * as React from 'react'
import clx from 'classnames'
import { FieldsetSelect, type Option } from '../../components/FieldsetSelect'

type Props = {
  className?: string
  label?: string

  // 1) select
  hour: {
    options: number[]
    value?: string | number | null
    name: string
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>
    handleChange?: (value: number, opt: { name: string, option: Option }) => void
    placeholder?: string
    disabled?: boolean
    error?: string | boolean
    required?: boolean
  }

  // 2) select
  minute: {
    options: number[]
    value?: string | number | null
    name: string
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>
    handleChange?: (value: number, opt: { name: string, option: Option }) => void
    placeholder?: string
    disabled?: boolean
    error?: string | boolean
    required?: boolean
  }

  // 3) readonly value (np. ": 06:00")
  readonly?: {
    value: React.ReactNode
    className?: string
  }
}

const to2 = (n: number) => String(n).padStart(2, '0')

const toOptions = (nums: number[]): Option[] =>
  nums.map((n) => ({
    key: n.toString(),
    label: to2(n),
    value: String(n),
  }))

export const TripleTimeLikeFields = ({
  label,
  className,
  hour,
  minute,
  readonly,
}: Props) => {
  const hourOptions = React.useMemo(
    () => toOptions(hour.options),
    [hour.options],
  )
  const minuteOptions = React.useMemo(
    () => toOptions(minute.options),
    [minute.options],
  )

  const handleHourChange = React.useCallback(
    (val: string, props: { name: string; option: Option }) => {
      const parsed = Number(val)
      if (Number.isFinite(parsed) && hour.handleChange) {
        hour.handleChange(parsed, { name: props.name, option: props.option })  
      }
    },
    [hour.handleChange],
  )

  const handleMinuteChange = React.useCallback(
    (val: string, props: { name: string; option: Option }) => {
      const parsed = Number(val)
       if (Number.isFinite(parsed) && minute.handleChange) {
        minute.handleChange(parsed, { name: props.name, option: props.option })  
      }
    },
    [minute.handleChange],
  )

  return (
    <div className={clx('tw-flex tw-items-center tw-gap-3', className)}>
      {label && (
        <label className="tw-group tw-mb-0.5 tw-px-1 tw-text-sm tw-font-medium">
          {/* {required && <span className="tw-mr-0.5 tw-text-error">*</span>} */}
          {label}
        </label>
      )}

      <div className="tw-w-[88px]">
        <FieldsetSelect
          caretSize="xs"
          placeholder={hour.placeholder}
          options={hourOptions}
          value={hour.value}
          name={hour.name}
          onChange={hour.onChange}
          handleChange={handleHourChange}
          disabled={hour.disabled}
          error={hour.error}
          required={hour.required}
        />
      </div>

      <div className="tw-w-[88px]">
        <FieldsetSelect
          caretSize="xs"
          placeholder={minute.placeholder}
          options={minuteOptions}
          value={minute.value}
          name={minute.name}
          onChange={minute.onChange}
          handleChange={handleMinuteChange}
          disabled={minute.disabled}
          error={minute.error}
          required={minute.required}
        />
      </div>

      {readonly && (
        <>
          <div className='tw-p-1'> - </div>
          <div
            className={clx(
              'tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-content-base tw-opacity-80',
              readonly.className,
            )}
          >
            {readonly.value}
          </div>
        </>
      )}
    </div>
  )
}
